import type React from 'react';
import type { CtaSurface, StylePack } from '@/themes/stylePack.types';
import type { EditorScreen } from '@/store/editor/types/screen';

/**
 * Прибитая нижняя кнопка (пак `cta.placement: 'fixed-bottom'`) и вид самой
 * кнопки из пака — повтор эталона Slimkit (spec shared/cta-button.md п.1–2, 6,
 * 8; shared/screen-container.md «Низ с кнопкой», «Что добавить» п.5).
 *
 * Всё выключено, пока пак не задал новое поле: у 12 прежних паков нет ни
 * 'fixed-bottom', ни typography/shadow/maxWidthPx, ни 'muted-brand-dark' —
 * `resolveCtaDock` даёт null, `ctaPackButtonStyle` — пустой объект.
 */

export const CTA_SURFACES: readonly CtaSurface[] = [
  'none',
  'solid',
  'solid-when-scroll',
  'fog',
];

/** Значения эталона, когда пак поле не задал. */
export const CTA_DEFAULT_BOTTOM_PX = 20;
export const CTA_DEFAULT_RESERVE_PX = 100;

export interface CtaDockConfig {
  surface: CtaSurface;
  bottomPx: number;
  reservePx: number;
  /**
   * Отступ задан своим полем воронки (`settings.quizCta.bottomOffset`): панель
   * держит его ровно, и правило CSS «от 920 px низ 32» его не перебивает
   * (CtaDock.tsx). Нет поля — признака нет, отступ как у пака.
   */
  exactBottom?: true;
}

function isSurface(value: unknown): value is CtaSurface {
  return (
    typeof value === 'string' &&
    (CTA_SURFACES as readonly string[]).includes(value)
  );
}

function nonNegative(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : fallback;
}

/**
 * Нужна ли прибитая кнопка и с какой подложкой; экран переопределяет подложку
 * пака. `placement` — уже выбранное положение (своё поле воронки
 * `settings.quizCta` сильнее пака, слово владельца 21.10.2026, quizCta.ts);
 * не передано — решает пак. Без пака подложка и отступы — значения эталона.
 *
 * `bottomOffset` — свой отступ кнопки от низа окна (`settings.quizCta`, уже
 * разобранный зажимом quizCta.ts): задан — кнопка ровно на нём, а запас под
 * содержимым меняется на ту же разницу с отступом пака (кнопка не закрывает
 * последний блок), но не ниже нуля.
 */
export function resolveCtaDock(
  pack: StylePack | undefined,
  screen: Pick<EditorScreen, 'ctaSurface'> | undefined,
  placement: StylePack['cta']['placement'] | undefined = pack?.cta.placement,
  bottomOffset?: number,
): CtaDockConfig | null {
  if (placement !== 'fixed-bottom') return null;
  const cta = pack?.cta;
  const surface = isSurface(screen?.ctaSurface)
    ? screen.ctaSurface
    : isSurface(cta?.surface)
      ? cta.surface
      : 'none';
  const bottomPx = nonNegative(cta?.bottomOffsetPx, CTA_DEFAULT_BOTTOM_PX);
  const reservePx = nonNegative(cta?.reservePx, CTA_DEFAULT_RESERVE_PX);
  if (bottomOffset === undefined) return { surface, bottomPx, reservePx };
  return {
    surface,
    bottomPx: bottomOffset,
    reservePx: Math.max(0, reservePx + bottomOffset - bottomPx),
    exactBottom: true,
  };
}

/** Обёртка блока «Кнопка Далее» в прибитой панели: без нижнего поля 16 px. */
export const DOCKED_WRAP_STYLE: React.CSSProperties = { marginBottom: 0 };

/** Затемнение 15 % чёрного поверх заливки неактивной кнопки (эталон `::after`). */
export const CTA_DISABLED_DARKEN = 'inset 0 0 0 100vmax rgba(0, 0, 0, 0.15)';

/**
 * Вид кнопки продвижения из пака: высота, поля, ширина, текст, тень и
 * затемнение неактивной. Идёт ПОСЛЕ стиля темы и ДО полей блока — свой
 * кегль/фон автора на блоке сильнее пака.
 *
 * Внутренняя тень рисуется под текстом, поэтому затемняется только заливка, а
 * текст остаётся белым — как у эталона, где затемнение лежит в `::after`.
 */
export function ctaPackButtonStyle(
  pack: StylePack | undefined,
  dimmed: boolean,
  disabledStyle: StylePack['cta']['disabledStyle'] | undefined = pack?.cta
    .disabledStyle,
): React.CSSProperties {
  // Вид выключенной — уже выбранный (поле воронки settings.quizCta сильнее
  // пака, quizCta.ts); без пака своё поле даёт только затемнение.
  const darken = dimmed && disabledStyle === 'muted-brand-dark';
  if (!pack) return darken ? { boxShadow: CTA_DISABLED_DARKEN } : {};
  const { cta } = pack;
  const style: React.CSSProperties = {};
  const hasLook =
    cta.typography !== undefined ||
    cta.shadow !== undefined ||
    cta.maxWidthPx !== undefined;
  if (hasLook) {
    style.height = cta.height;
    style.minHeight = cta.height;
    style.padding = '0 20px';
    style.boxSizing = 'border-box';
    if (typeof cta.maxWidthPx === 'number') style.maxWidth = cta.maxWidthPx;
    const t = cta.typography;
    if (t?.sizePx !== undefined) style.fontSize = t.sizePx;
    if (t?.weight !== undefined) style.fontWeight = t.weight;
    if (t?.lineHeightPx !== undefined) style.lineHeight = `${t.lineHeightPx}px`;
    if (t?.letterSpacingPx !== undefined)
      style.letterSpacing = `${t.letterSpacingPx}px`;
  }
  const shadows: string[] = [];
  if (darken) shadows.push(CTA_DISABLED_DARKEN);
  if (cta.shadow) shadows.push(cta.shadow);
  if (shadows.length > 0) style.boxShadow = shadows.join(', ');
  return style;
}
